import React, { useMemo, useState } from 'react';
import { User } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import useClientExports, { ClientExportFile, ClientExportRecord } from '../hooks/useClientExports';
import { getExportDownloadUrl } from '../lib/callables';
import './ExportsHistory.css';

type Props = {
  user: User;
};

function typeMeta(type: string): { label: string; noun: string } {
  if (type === 'rawPhoto') return { label: 'Raw Photos', noun: 'photos' };
  if (type === 'annotatedPhoto') return { label: 'Annotated Photos', noun: 'photos' };
  if (type === 'rawVideo') return { label: 'Raw Video', noun: 'clips' };
  return { label: type || 'Export', noun: 'items' };
}

/** Format byte counts for file rows — no external dependency. */
function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let n = bytes;
  let i = -1;
  do {
    n /= 1024;
    i += 1;
  } while (n >= 1024 && i < units.length - 1);
  const digits = n >= 10 || i === 0 ? 0 : 1;
  return `${n.toFixed(digits)} ${units[i]}`;
}

/**
 * Display order only — sorts a copy; never mutates hook state or Firestore.
 * annotations* first; then by numeric portion ascending; no-digit names last A–Z.
 */
function sortExportFilesForDisplay(files: ClientExportFile[]): ClientExportFile[] {
  const digitRe = /(\d+)/;
  return [...files].sort((a, b) => {
    const aAnn = a.name.startsWith('annotations');
    const bAnn = b.name.startsWith('annotations');
    if (aAnn && !bAnn) return -1;
    if (!aAnn && bAnn) return 1;
    if (aAnn && bAnn) return a.name.localeCompare(b.name);

    const aMatch = a.name.match(digitRe);
    const bMatch = b.name.match(digitRe);
    const aNum = aMatch ? Number(aMatch[1]) : null;
    const bNum = bMatch ? Number(bMatch[1]) : null;

    if (aNum != null && bNum != null && aNum !== bNum) return aNum - bNum;
    if (aNum != null && bNum == null) return -1;
    if (aNum == null && bNum != null) return 1;
    return a.name.localeCompare(b.name);
  });
}

function isMultiFileReady(record: ClientExportRecord): boolean {
  return record.status === 'ready' && Array.isArray(record.files) && record.files.length > 1;
}

function isLegacyOrSingleDownload(record: ClientExportRecord): boolean {
  if (record.status === 'building' || record.status === 'failed') return false;
  if (record.status === 'ready' && Array.isArray(record.files) && record.files.length > 1) {
    return false;
  }
  // ready with 0–1 files, or no status (legacy) — one Download button, no fileName
  return true;
}

export default function ExportsHistory({ user }: Props) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { exports, loading, error: loadError } = useClientExports(user.uid, 50);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stats = useMemo(() => {
    const totalExports = exports.length;
    const itemsExported = exports.reduce((sum, e) => sum + e.photoCount, 0);
    const photos = exports
      .filter((e) => e.type === 'rawPhoto' || e.type === 'annotatedPhoto')
      .reduce((sum, e) => sum + e.photoCount, 0);
    const videoClips = exports
      .filter((e) => e.type === 'rawVideo')
      .reduce((sum, e) => sum + e.photoCount, 0);
    return { totalExports, itemsExported, photos, videoClips };
  }, [exports]);

  const handleDownload = async (exportId: string, fileName?: string) => {
    setError(null);
    const key = fileName ? `${exportId}::${fileName}` : exportId;
    setDownloadingKey(key);
    try {
      const result = await getExportDownloadUrl(exportId, fileName);
      window.open(result.downloadUrl, '_blank');
    } catch (err: any) {
      console.error('Failed to fetch export ZIP', err);
      setError(err?.message || 'Unable to fetch the ZIP. It may no longer be available.');
    } finally {
      setDownloadingKey(null);
    }
  };

  const displayError = error || loadError;

  const renderActions = (record: ClientExportRecord) => {
    if (record.status === 'building') {
      const done = record.progress?.done ?? 0;
      const total = record.progress?.total ?? 0;
      return (
        <span className="status-badge status-badge--building" style={{ color: colors.secondary }}>
          Preparing {done}/{total}
        </span>
      );
    }

    if (record.status === 'failed') {
      return (
        <span className="export-failed" style={{ color: colors.error || '#c62828' }}>
          {record.error || 'Export failed'}
        </span>
      );
    }

    if (isMultiFileReady(record)) {
      const orderedFiles = sortExportFilesForDisplay(record.files as ClientExportFile[]);
      return (
        <div className="file-download-list">
          {orderedFiles.map((file) => {
            const key = `${record.exportId}::${file.name}`;
            return (
              <div key={file.name} className="file-download-row">
                <div className="file-download-meta">
                  <span className="file-download-name">{file.name}</span>
                  <span className="file-download-size">{formatBytes(file.bytes)}</span>
                </div>
                <button
                  className="ghost-btn"
                  disabled={downloadingKey === key}
                  onClick={() => handleDownload(record.exportId, file.name)}
                >
                  {downloadingKey === key ? 'Fetching…' : 'Download'}
                </button>
              </div>
            );
          })}
        </div>
      );
    }

    if (isLegacyOrSingleDownload(record)) {
      return (
        <div className="history-actions">
          <button
            className="ghost-btn"
            disabled={downloadingKey === record.exportId}
            onClick={() => handleDownload(record.exportId)}
          >
            {downloadingKey === record.exportId ? 'Fetching…' : 'Download'}
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="exports-history" style={{ background: colors.background, color: colors.text }}>
      <div className="history-header">
        <div>
          <h1>Exports History</h1>
          <p>Track PIX spend and re-download your dataset exports.</p>
        </div>
        <button
          className="primary-btn"
          style={{ background: colors.primary }}
          onClick={() => navigate('/library')}
        >
          Export from Library
        </button>
      </div>

      {displayError && (
        <div className="alert alert--error">
          {displayError}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {loading ? (
        <div className="history-loading">Loading exports...</div>
      ) : exports.length === 0 ? (
        <div className="empty-state">
          <p>You haven’t exported any datasets yet.</p>
          <button className="secondary-btn" onClick={() => navigate('/library')}>
            Go to Library
          </button>
        </div>
      ) : (
        <>
          <div className="stats-row">
            {[
              { label: 'TOTAL EXPORTS', value: stats.totalExports.toLocaleString() },
              { label: 'ITEMS EXPORTED', value: stats.itemsExported.toLocaleString() },
              { label: 'PHOTOS', value: stats.photos.toLocaleString() },
              { label: 'VIDEO CLIPS', value: stats.videoClips.toLocaleString() },
            ].map((tile) => (
              <article
                key={tile.label}
                className="dashboard-panel"
                style={{
                  borderColor: colors.border,
                  flex: '1 1 160px',
                  minWidth: 0,
                  padding: '16px 18px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    color: colors.secondary,
                    marginBottom: 6,
                    minHeight: 32,
                    lineHeight: 1.25,
                  }}
                >
                  {tile.label}
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: colors.text, lineHeight: 1.1 }}>
                  {tile.value}
                </div>
              </article>
            ))}
          </div>

          <div className="history-table" style={{ borderColor: colors.border }}>
            <table>
              <thead>
                <tr>
                  <th>Export</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Items</th>
                  <th>Skipped</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {exports.map((record) => {
                  const meta = typeMeta(record.type);
                  const dateLabel = record.createdAt
                    ? formatDate(record.createdAt)
                    : record.exportDate || '—';
                  return (
                    <tr key={record.id}>
                      <td>
                        <strong>{record.exportId}</strong>
                        <div className="subtle">{meta.label}</div>
                        {record.status === 'building' && (
                          <div className="subtle">Status: building</div>
                        )}
                        {record.status === 'failed' && (
                          <div className="subtle">Status: failed</div>
                        )}
                      </td>
                      <td>
                        <span className="type-badge">{meta.label}</span>
                      </td>
                      <td>{dateLabel}</td>
                      <td>
                        {record.photoCount.toLocaleString()} {meta.noun}
                      </td>
                      <td>
                        {record.skipped > 0 ? (
                          <span className="skipped-warn">{record.skipped}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{renderActions(record)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function formatDate(value?: Date) {
  if (!value) return '—';
  return value.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
